import { makeAutoObservable, runInAction } from 'mobx'
import { restClient } from '@/Core'
import type { Notify } from '@/Ship/Contracts/Notify'

interface Delay {
    uuid: string
    status: string
    driverComment: string
    receivedAt: string
    escalatedAt?: string | null
    resolvedAt?: string | null
    resolverComment?: string | null
    resolverDispatcher?: { fullName?: string; title?: string } | null
}

/**
 * Local store of `TaskDelaysCell`.
 *
 * Owns the popup modal state, the in-cell resolve form state, and a local copy
 * of `delays` that is optimistically updated after a successful resolve.
 *
 * UX note: `resolvingUuid` switches a specific row inside the modal table into
 * an inline comment-form. We do NOT use the shared `Ship/Modals/ResolveDelayModal`
 * here because that one is a full screen modal — the TaskDelaysCell variant
 * intentionally keeps the dispatcher in the table view while resolving.
 */
export class TaskDelaysCellStore {
    open = false
    delays: Delay[] = []
    resolvingUuid: string | null = null
    loading = false

    constructor() {
        makeAutoObservable(this)
    }

    /**
     * Refresh the cell's local `delays` from the freshest props.
     * Called on mount and whenever the incoming `delays` prop changes.
     */
    syncFromProps(delays: Delay[] | undefined | null): void {
        this.delays = delays ?? []
    }

    setOpen(v: boolean): void {
        this.open = v
        if (!v) {
            this.resolvingUuid = null
        }
    }

    startResolving(uuid: string): void {
        this.resolvingUuid = uuid
    }

    cancelResolving(): void {
        this.resolvingUuid = null
    }

    canResolve(status: string): boolean {
        return status === 'received' || status === 'escalated'
    }

    /**
     * POST resolve to the backend. On success — optimistically mark the affected
     * delay as `resolved` locally and ask the caller (via `reload`) to refresh
     * the request from the server.
     *
     * The comment is passed in from the form's local React state (not stored as
     * an observable) so typing it does not re-render the whole delays table.
     */
    async resolve(uuid: string, comment: string, notify: Notify, reload?: () => void, outcome: 'resolved' | 'not_resolved' = 'resolved'): Promise<boolean> {
        const trimmed = comment.trim()
        if (!trimmed) {
            notify.error('Введіть коментар для вирішення затримки')
            return false
        }
        this.loading = true
        try {
            await restClient.post(`/services/assignment-execution-delays/${uuid}/resolve`, {
                json: { resolverComment: trimmed, outcome },
            })
            notify.success(outcome === 'resolved' ? 'Затримку вирішено' : 'Результат збережено')
            runInAction(() => {
                this.delays = this.delays.map(d => d.uuid === uuid
                    ? { ...d, status: outcome, resolvedAt: new Date().toISOString(), resolverComment: trimmed }
                    : d
                )
                this.resolvingUuid = null
            })
            reload?.()
            return true
        } catch (e: any) {
            notify.error(e?.response?.data?.detail ?? 'Помилка при вирішенні затримки')
            return false
        } finally {
            runInAction(() => { this.loading = false })
        }
    }
}
